// src/index.ts
import { base64urlEncode } from "@phantom/base64url";
import { getTransactionEncoder } from "@solana/transactions";
import { Buffer as Buffer2 } from "buffer";
import { Transaction as EthersTransaction, getAddress } from "ethers";

// src/response-parsers.ts
import { base64urlDecode } from "@phantom/base64url";
import { getExplorerUrl } from "@phantom/constants";
import { isEthereumChain } from "@phantom/utils";
import { Transaction, VersionedTransaction } from "@solana/web3.js";
import bs58 from "bs58";
import { Buffer } from "buffer";
function parseSignMessageResponse(base64Response, networkId) {
  const networkPrefix = networkId.split(":")[0].toLowerCase();
  switch (networkPrefix) {
    case "solana":
      return parseSolanaSignatureResponse(base64Response);
    case "eip155":
    case "ethereum":
      return parseEVMSignatureResponse(base64Response);
    case "sui":
      return parseSuiSignatureResponse(base64Response);
    case "bip122":
    case "bitcoin":
      return parseBitcoinSignatureResponse(base64Response);
    default:
      return {
        signature: base64Response,
        rawSignature: base64Response
      };
  }
}
function parseTransactionResponse(base64RawTransaction, networkId, hash) {
  let rawTransaction = base64RawTransaction;
  if (isEthereumChain(networkId)) {
    try {
      const txBytes = base64urlDecode(base64RawTransaction);
      rawTransaction = "0x" + Buffer.from(txBytes).toString("hex");
    } catch (error) {
      rawTransaction = base64RawTransaction.startsWith("0x") ? base64RawTransaction : "0x" + base64RawTransaction;
    }
  }
  if (hash) {
    return {
      hash,
      rawTransaction,
      blockExplorer: getExplorerUrl(networkId, "transaction", hash)
    };
  } else {
    return {
      rawTransaction
    };
  }
}
function parseSolanaSignatureResponse(base64Response) {
  try {
    const signatureBytes = base64urlDecode(base64Response);
    const signature = bs58.encode(signatureBytes);
    return {
      signature,
      rawSignature: base64Response
    };
  } catch (error) {
    return {
      signature: base64Response,
      rawSignature: base64Response
    };
  }
}
function parseEVMSignatureResponse(base64Response) {
  const signatureBytes = base64urlDecode(base64Response);
  const signature = "0x" + Buffer.from(signatureBytes).toString("hex");
  return {
    signature,
    rawSignature: base64Response
  };
}
function parseSuiSignatureResponse(base64Response) {
  try {
    const signatureBytes = base64urlDecode(base64Response);
    const signature = Buffer.from(signatureBytes).toString("base64");
    return {
      signature,
      rawSignature: base64Response
    };
  } catch (error) {
    return {
      signature: base64Response,
      rawSignature: base64Response
    };
  }
}
function parseBitcoinSignatureResponse(base64Response) {
  try {
    const signatureBytes = base64urlDecode(base64Response);
    const signature = Buffer.from(signatureBytes).toString("hex");
    return {
      signature,
      rawSignature: base64Response
    };
  } catch (error) {
    return {
      signature: base64Response,
      rawSignature: base64Response
    };
  }
}
function parseSolanaSignedTransaction(base64RawTransaction) {
  try {
    const transactionBytes = base64urlDecode(base64RawTransaction);
    return deserializeSolanaTransaction(transactionBytes);
  } catch (error) {
    return null;
  }
}
function deserializeSolanaTransaction(transactionBytes) {
  try {
    const transaction = Transaction.from(transactionBytes);
    return transaction;
  } catch (legacyError) {
    if (legacyError instanceof Error && legacyError.message.includes("Versioned messages")) {
      const versionedTransaction = VersionedTransaction.deserialize(transactionBytes);
      return versionedTransaction;
    }
    throw legacyError;
  }
}

// src/index.ts
async function parseToKmsTransaction(transaction, networkId) {
  const networkPrefix = networkId.split(":")[0].toLowerCase();
  switch (networkPrefix) {
    case "solana":
      return parseSolanaTransactionToBase64Url(transaction);
    case "ethereum":
    case "eip155":
    case "polygon":
    case "optimism":
    case "arbitrum":
    case "base":
      return parseEVMTransactionToHex(transaction);
    case "sui":
      return await parseSuiTransactionToBase64Url(transaction);
    case "bitcoin":
      return parseBitcoinTransactionToBase64Url(transaction);
    default:
      throw new Error(`Unsupported network: ${networkPrefix}`);
  }
}
function parseSolanaTransactionToBase64Url(transaction) {
  if (transaction?.messageBytes != null) {
    return {
      parsed: base64urlEncode(transaction.messageBytes),
      originalFormat: "@solana/kit"
    };
  }
  if (typeof transaction?.serialize === "function") {
    const serialized = transaction.serialize({
      requireAllSignatures: false,
      verifySignatures: false
    });
    return {
      parsed: base64urlEncode(serialized),
      originalFormat: "@solana/web3.js"
    };
  }
  if (transaction instanceof Uint8Array) {
    return {
      parsed: base64urlEncode(transaction),
      originalFormat: "bytes"
    };
  }
  if (typeof transaction === "string") {
    try {
      const bytes = Buffer2.from(transaction, "base64");
      return {
        parsed: base64urlEncode(new Uint8Array(bytes)),
        originalFormat: "base64"
      };
    } catch {
      throw new Error("Unsupported Solana transaction format");
    }
  }
  throw new Error("Unsupported Solana transaction format");
}
function parseEVMTransactionToHex(transaction) {
  if (typeof transaction === "string" && transaction.startsWith("0x")) {
    return {
      parsed: transaction,
      originalFormat: "hex"
    };
  }
  if (transaction?.serialize && typeof transaction.serialize === "function") {
    const serialized = transaction.serialize();
    const hex = serialized.startsWith("0x") ? serialized : "0x" + serialized;
    return {
      parsed: hex,
      originalFormat: "ethers"
    };
  }
  if (transaction instanceof Uint8Array) {
    const hex = "0x" + Buffer2.from(transaction).toString("hex");
    return {
      parsed: hex,
      originalFormat: "bytes"
    };
  }
  if (transaction && typeof transaction === "object" && (transaction.to || transaction.data || transaction.from)) {
    try {
      const { from, gas, ...txForSerialization } = transaction;
      if (gas) {
        txForSerialization.gasLimit = gas;
      }
      if (!txForSerialization.gasLimit) {
        if (txForSerialization.to && txForSerialization.value && !txForSerialization.data) {
          txForSerialization.gasLimit = "0x5208";
        }
      }
      if (txForSerialization.to && typeof txForSerialization.to === "string") {
        try {
          txForSerialization.to = getAddress(txForSerialization.to);
        } catch {
          txForSerialization.to = txForSerialization.to.toLowerCase();
        }
      }
      const serialized = EthersTransaction.from(txForSerialization).unsignedSerialized;
      const hex = serialized.startsWith("0x") ? serialized : "0x" + serialized;
      return {
        parsed: hex,
        originalFormat: "json"
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      const txKeys = transaction ? Object.keys(transaction).join(", ") : "N/A";
      const txValues = transaction ? JSON.stringify(transaction, null, 2) : "N/A";
      throw new Error(
        `Failed to RLP encode EVM transaction: ${errorMessage}.
Transaction keys: [${txKeys}].
Transaction: ${txValues}
Please ensure the transaction object includes required fields (to, value, chainId, gasLimit or gasPrice, etc.)`
      );
    }
  }
  throw new Error(
    "Unsupported EVM transaction format. Expected hex string, bytes, or transaction object with 'to', 'data', or 'from' fields."
  );
}
async function parseSuiTransactionToBase64Url(transaction) {
  if (transaction?.serialize && typeof transaction.serialize === "function") {
    const serialized = transaction.serialize();
    return {
      parsed: base64urlEncode(serialized),
      originalFormat: "sui-sdk"
    };
  }
  if (transaction instanceof Uint8Array) {
    return {
      parsed: base64urlEncode(transaction),
      originalFormat: "bytes"
    };
  }
  if (transaction?.build && typeof transaction.build === "function") {
    const built = await transaction.build();
    if (built?.serialize && typeof built.serialize === "function") {
      const serialized = built.serialize();
      return {
        parsed: base64urlEncode(serialized),
        originalFormat: "transaction-block"
      };
    }
  }
  throw new Error("Unsupported Sui transaction format");
}
function parseBitcoinTransactionToBase64Url(transaction) {
  if (transaction?.toBuffer && typeof transaction.toBuffer === "function") {
    const buffer = transaction.toBuffer();
    return {
      parsed: base64urlEncode(new Uint8Array(buffer)),
      originalFormat: "bitcoinjs-lib"
    };
  }
  if (transaction instanceof Uint8Array) {
    return {
      parsed: base64urlEncode(transaction),
      originalFormat: "bytes"
    };
  }
  if (typeof transaction === "string") {
    const bytes = new Uint8Array(Buffer2.from(transaction, "hex"));
    return {
      parsed: base64urlEncode(bytes),
      originalFormat: "hex"
    };
  }
  throw new Error("Unsupported Bitcoin transaction format");
}
function parseSolanaKitTransactionToSolanaWeb3js(transaction) {
  const serialized = getTransactionEncoder().encode(transaction);
  const fakeVersioned = {
    serialize() {
      return new Uint8Array(serialized);
    }
  };
  return fakeVersioned;
}
export {
  deserializeSolanaTransaction,
  parseSignMessageResponse,
  parseSolanaKitTransactionToSolanaWeb3js,
  parseSolanaSignedTransaction,
  parseToKmsTransaction,
  parseTransactionResponse
};
